import { v4 as uuidv4 } from 'uuid';

import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmFormSuspensionEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';
import { convertSuspensionEventsToFormData } from 'KnmErknmPackage/MainModule/common/convertSuspensionEventsToFormData';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { sortSuspensionEvents } from 'KnmErknmPackage/MainModule/common/sortSuspensionEvents';
import { IKnmDictionary } from 'APIPackage/KnmModule/interfaces/IKnmDictionary';
import { getKnmErknmParticipantPositionsSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IUavTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUavTypeSelectOption';
import {
  getDistrictSelectOptions,
  getUavTypeSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { createPmFormConductingSelectOption } from 'CorePackage/CatalogsModule/common/createPmFormConductingSelectOption';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';

import { getKnmPmInitialFormValues } from '../selectors/knmPmSelectors';
import { convertEventExtensionToFormData } from './convertEventExtensionToFormData';

export const getInitialObject = (object?: IKnmErknmFormObject | undefined) => ({
  address: object?.address,
  objectType: object?.objectType,
  objectKind: object?.objectKind,
  objectSubKind: object?.objectSubKind,
  riskCategory: object?.riskCategory,
  dangerClass: object?.dangerClass,
  guid: object?.guid || undefined,
  ervkNumber: object?.ervkNumber || undefined,
  ervkId: object?.ervkId || undefined,
  tmpErvkCosId: object?.tmpErvkCosId || undefined,
  cosId: object?.cosId || undefined,
  cosVersion: object?.cosVersion || undefined,
  name: object?.name || undefined,
  streetGuid: object?.streetGuid || undefined,
  houseGuid: object?.houseGuid || undefined,
  roomGuid: object?.roomGuid || undefined,
  steadGuid: object?.steadGuid || undefined,
  addressFns: object?.addressFns || undefined,
  autocomplete: typeof object?.autocomplete === 'boolean' ? object.autocomplete : undefined,
  dateCreate: object?.dateCreate || undefined,
  geolocation: object?.geolocation || undefined,
  listId: object?.listId || object?.guid || uuidv4(),
  isNew: object?.isNew || false,
  manualAddress: object?.manualAddress || undefined,
  region: object?.region || undefined,
  fiasRegion: object?.fiasRegion || undefined,
  regionName: object?.regionName || undefined,
  notInFIAS: object?.notInFIAS || false,
  isErrorFiasValue: object?.isErrorFiasValue || false,
  inspectionResults: object?.inspectionResults || undefined,
  organizationErvkId: object?.organizationErvkId || undefined,
  updating: object?.updating || undefined,
  isFilledFromLink: object?.isFilledFromLink || undefined,
});

export const processSuspensionEventsAndPeriodExtension = (
  initialEvents: IKnmEvent248[] | null | undefined,
  events: IKnmErknmFormEvent[] | undefined,
  state: IRootState,
) => {
  const suspensionEvents: IKnmErknmFormSuspensionEvent[] = [];
  let periodExtension: IKnmPmFormExtension | undefined;

  initialEvents?.forEach((event, index) => {
    suspensionEvents.push(...convertSuspensionEventsToFormData(event, events?.[index], index, state));
    const selectOption = event.event?.dictId
      ? getDictionarySelectOptionWithState<DictionaryUUIDsEnum.EVENT_TYPE>(
          state,
          event.event?.dictId,
          event.event?.dictVersionId,
        )
      : undefined;

    if (selectOption?.title === 'Инструментальное обследование') {
      periodExtension = convertEventExtensionToFormData(event.periodExtension);
    }
  });

  sortSuspensionEvents(suspensionEvents);

  return {
    suspensionEvents,
    periodExtension,
  };
};

export const getSignerPositionSelectOption = (
  initialSignerPosition: IKnmDictionary | null | undefined,
  state: IRootState,
) => {
  const option1 = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.POSITIONS>(
    state,
    initialSignerPosition?.dictId,
    initialSignerPosition?.dictVersionId,
  );

  if (option1) return option1;

  const positions = getKnmErknmParticipantPositionsSelectOptions(state);
  const option2 = positions?.find(
    i =>
      i.value.dictVersionId === initialSignerPosition?.dictVersionId &&
      i.value.dictId === initialSignerPosition?.dictId,
  );

  if (option2) return option2;

  return getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.POSITIONS>(
    state,
    initialSignerPosition?.dictId,
    initialSignerPosition?.dictVersionId,
  );
};

export const knmPmInitFormValuesSagaHelper = (knmData: IKnm, state: IRootState) => {
  const settings = getAdminFetchSettings(state);

  const initialEntities = {
    isHasCollaboratingOrganization: knmData.pmErknm?.isHasCollaboratingOrganization,
    knoOrganization: knmData?.pmErknm?.knoOrganization,
    initialSupervisionType: knmData?.pmErknm?.supervisionType,
    initialPmType: knmData?.pmErknm?.pmType,
    initialSignerPosition: knmData.pmErknm?.signerPosition,
    initialInspectors: knmData?.pmErknm?.inspectors,
    initialExperts: knmData?.pmErknm?.experts,
    initialReasons: knmData?.pmErknm?.reasons,
    initialEvents: knmData?.pmErknm?.events,
    initialCollaboratingOrganizations: knmData?.pmErknm?.collaboratingOrganizations,
    initialApproveDocs: knmData.pmErknm?.approveDocs,
    initialTakenMeasures: knmData.pmErknm?.takenMeasures,
    initialDocTarget: knmData?.pmErknm?.docTarget,
    initialAgreements: knmData?.pmErknm?.agreements,
    initialAgreementOnApproval: knmData?.pmErknm?.agreementOnApproval,
    initialFormValues: getKnmPmInitialFormValues(state),
    initialProsecutorOrganization: knmData?.prosecutorOrganization,
    initialFederalLaw: knmData?.federalLaw,
    pmForm: knmData?.pmErknm?.pmForm,
    initialObjects: knmData?.pmErknm?.objects,
    initialRequirementsNpa: knmData?.pmErknm?.requirements,
    initialAccessPrivilege: knmData.pmErknm?.accessPrivilege,
    initialNotifications: knmData?.subjectNotifications,
    initialReject: knmData?.pmErknm?.pmRejects,
    initialRescheduling: knmData?.pmErknm?.rescheduling,
    initialAbrogation: knmData?.pmErknm?.abrogation,
    initialSubject: knmData.pmErknm?.subject,
  };

  /*
    В течение недели на проде значения для поля "Должность лица, подписавшего паспорт ПМ" заполнялись из неправильного справочника,
    поэтому было принято решение оставить реализацию заполнения поля из словаря participant_positions, чтобы не сломать отображение в уже заполненных ПМ
   */
  const signerPositionSelectOption: IDictionaryPositionSelectOption | undefined = getSignerPositionSelectOption(
    initialEntities.initialSignerPosition,
    state,
  );

  const selectOptions = {
    knoOrganizationSelectOption: getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.KNO_ORGANIZATIONS>(
      state,
      initialEntities.knoOrganization?.dictId,
      initialEntities.knoOrganization?.dictVersionId,
    ),
    supervisionTypeSelectOption: getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUPERVISION_TYPE>(
      state,
      initialEntities.initialSupervisionType?.dictId,
      initialEntities.initialSupervisionType?.dictVersionId,
    ),
    pmTypeSelectOption: getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PROF_KIND>(
      state,
      initialEntities.initialPmType?.dictId,
      initialEntities.initialPmType?.dictVersionId,
    ),
    uavTypeSelectOptions: getUavTypeSelectOptions(state) as IUavTypeSelectOption[],
    signerPositionSelectOption,
    warningContentPositionCancelSelectOption: getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.POSITIONS>(
      state,
      knmData.pmErknm?.warningContentPositionCancel?.dictId,
      knmData.pmErknm?.warningContentPositionCancel?.dictVersionId,
    ) as IDictionaryPositionSelectOption | undefined,
    pmFormSelectOption: initialEntities.pmForm
      ? createPmFormConductingSelectOption({ ...initialEntities.pmForm })
      : undefined,
    districtSelectOptions: getDistrictSelectOptions(state),
  };

  const conditions = {
    isUseTransferringAuthorities: isLaterThanAdminSetting(
      initialEntities.initialFormValues.created,
      settings,
      AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
    ),

    isOn411: isLaterThanAdminSetting(
      initialEntities.initialFormValues.created,
      settings,
      AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1,
    ),
  };

  return {
    initialEntities,
    selectOptions,
    conditions,
  };
};
